import { Stack, StackProps, styled } from "@mui/material";
import {
  GreenButton,
  LoadingSkeleton,
  ReportButton,
  Row,
  TextMd,
  Tooltip,
  XsText,
  XsTextMd,
} from "Components";
import { Fragment, memo, useMemo } from "react";
import { useIntl } from "react-intl";
import { scrollToElement } from "helpers/browser";
import useGetData from "hooks/useGetData";
import { apiGetCompanyDetail } from "services/api/company";
import { SupplierActivityTagType, SupplierType } from "types/supplier";
import { SendMessageButton } from "Components/button/index";
import SupplierInfors from "Components/infos/SupplierInfors";
import { REPORT_TYPE } from "constants/data";
import UserAvatar from "Components/avatar/UserAvatar";
import { COLORS } from "constants/layout";

const ContactSupplier = (props: StackProps & { supplierId?: string }) => {
  const { supplierId, ...rest } = props;
  const t = useIntl();

  const { data, isLoading } = useGetData({
    promise: () => (supplierId ? apiGetCompanyDetail(supplierId) : null),
    deps: [supplierId],
  });

  const supplierDetail: SupplierType = useMemo(
    () => data?.data?.farm || {},
    [data]
  );

  const { salutation, user_image, farmer_name, position } = supplierDetail;

  const activityTags = useMemo(
    () => supplierDetail?.activity_tags || [],
    [supplierDetail.activity_tags]
  );

  return (
    <StyledContactSupplier spacing={2} {...rest}>
      <Row justifyContent="space-between">
        <TextMd component="h2">
          {t.formatMessage({ id: "supplier_information" })}
        </TextMd>
        <ReportButton
          type={REPORT_TYPE.supplier}
          title={supplierDetail.farm_name}
          reference_name={supplierId}
        />
      </Row>

      {isLoading && <LoadingSkeleton />}
      {!isLoading && (
        <Fragment>
          <SupplierInfors company={supplierDetail} nameComponent="h3" />

          {activityTags.length > 0 && (
            <Stack gap={1} direction="row" flexWrap="wrap">
              {activityTags.map((tag: SupplierActivityTagType, i: number) => (
                <Tooltip key={i} title={tag?.description}>
                  <div>
                    <XsText className="tag-chip">{tag?.tag_name}</XsText>
                  </div>
                </Tooltip>
              ))}
            </Stack>
          )}

          <Row justifyContent="center">
            <GreenButton onClick={() => scrollToElement("inquiry-form")}>
              {t.formatMessage({ id: "inquiry_now" })}
            </GreenButton>
            <SendMessageButton identity={supplierDetail?.identity} />
          </Row>
        </Fragment>
      )}
    </StyledContactSupplier>
  );
};

const StyledContactSupplier = styled(Stack)({
  fontSize: 16,
  ".get_card_button": {
    "&.Mui-disabled": {
      opacity: 0.6,
    },
  },
  ".actions > *": {
    flex: 1,
    padding: "6px 12px",
    minHeight: 48,
  },
  ".send-inq-btn": {},
});

export default memo(ContactSupplier);
